import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type MenuTooltipProps = {
    label?: string;
    enabled?: boolean;
    children: React.ReactElement;
};

type TooltipState = {
    top: number;
    left: number;
    visible: boolean;
};

export default function MenuTooltip({ label, enabled = true, children }: MenuTooltipProps) {
    const ref = useRef<HTMLElement | null>(null);
    const [state, setState] = useState<TooltipState>({ top: 0, left: 0, visible: false });
    const isActive = enabled && !!label;

    const show = useCallback(() => {
        if (!isActive || !ref.current) {
            return;
        }

        const rect = ref.current.getBoundingClientRect();
        setState({
            top: rect.top + rect.height / 2,
            left: rect.right + 12,
            visible: true,
        });
    }, [isActive]);

    const hide = useCallback(() => {
        setState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    }, []);

    useEffect(() => {
        if (!state.visible) {
            return;
        }

        const handleScroll = () => hide();
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [hide, state.visible]);

    const childProps = {
        ref,
        onMouseEnter: (event: React.MouseEvent) => {
            children.props.onMouseEnter?.(event);
            show();
        },
        onMouseLeave: (event: React.MouseEvent) => {
            children.props.onMouseLeave?.(event);
            hide();
        },
        onFocus: (event: React.FocusEvent) => {
            children.props.onFocus?.(event);
            show();
        },
        onBlur: (event: React.FocusEvent) => {
            children.props.onBlur?.(event);
            hide();
        },
    };

    return (
        <>
            {React.cloneElement(children as React.ReactElement<any>, childProps as any)}
            {state.visible &&
                isActive &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="sf-menu-tooltip fixed z-[10002] pointer-events-none whitespace-nowrap rounded-xl px-3 py-2 text-xs font-medium"
                        style={{ top: state.top, left: state.left, transform: 'translateY(-50%)' }}
                        role="tooltip"
                    >
                        {label}
                    </div>,
                    document.body,
                )}
        </>
    );
}
